/**
 * What the stack view may show of a StackManifest: names and sources, never values.
 *
 * The manifest is parsed with the contract schema (unknown fields are stripped, unknown secret source types fail),
 * then copied field by field into a view model that has nowhere to put a value. `vars` keep their keys only: they
 * are meant to be plain text, but a secret pasted into one by mistake still never reaches the page.
 */
import { StackManifestSchema, type SecretSource } from "@botallion/contracts";
import { parse as parseToml } from "smol-toml";

export interface StackSecretView {
  name: string;
  source: { type: SecretSource["type"]; storeId: string; secretName: string; previewSecretName: string };
}

export interface StackWorkerView {
  name: string;
  entry: string;
  routes: string[];
  crons: string[];
  /** Keys only. */
  varNames: string[];
  bindings: { name: string; kind: "d1" | "r2" | "kv" | "service"; target: string }[];
  secrets: StackSecretView[];
}

export interface StackView {
  name: string;
  workers: StackWorkerView[];
  deployOrder: string[];
  previewNaming: string;
  productionNaming: string;
}

export type StackResult = { ok: true; stack: StackView } | { ok: false; error: string };

function secretSourceView(source: SecretSource): StackSecretView["source"] {
  switch (source.type) {
    case "cf-secrets-store":
      return {
        type: source.type,
        storeId: source.storeId,
        secretName: source.secretName,
        previewSecretName: source.previewSecretName ?? source.secretName,
      };
  }
}

/** Parse a manifest (an object, JSON text or TOML text) into what the stack view may show. */
export function stackView(input: unknown): StackResult {
  let raw = input;
  if (typeof input === "string") {
    try {
      raw = input.trimStart().startsWith("{") ? JSON.parse(input) : parseToml(input);
    } catch (e) {
      return { ok: false, error: `could not read the stack manifest: ${(e as Error).message.split("\n")[0]}` };
    }
  }
  const parsed = StackManifestSchema.safeParse(raw);
  if (!parsed.success) return { ok: false, error: `the stack manifest does not match the contract (${parsed.error.issues.length} issue(s))` };
  const m = parsed.data;
  return {
    ok: true,
    stack: {
      name: m.name,
      deployOrder: [...m.deployOrder],
      previewNaming: m.previewNaming,
      productionNaming: m.productionNaming ?? "{stack}-{resource}",
      workers: m.workers.map((w) => ({
        name: w.name,
        entry: w.entry,
        routes: [...w.routes],
        crons: [...(w.crons ?? [])],
        varNames: Object.keys(w.vars ?? {}),
        bindings: [
          ...m.d1.filter((d) => d.workers.includes(w.name)).map((d) => ({ name: d.binding, kind: "d1" as const, target: d.name })),
          ...m.r2.filter((d) => d.workers.includes(w.name)).map((d) => ({ name: d.binding, kind: "r2" as const, target: d.name })),
          ...m.kv.filter((d) => d.workers.includes(w.name)).map((d) => ({ name: d.binding, kind: "kv" as const, target: d.name })),
          ...m.services.filter((s) => s.worker === w.name).map((s) => ({ name: s.binding, kind: "service" as const, target: s.service })),
        ],
        secrets: (w.secrets ?? []).map((s) => ({ name: s.name, source: secretSourceView(s.source) })),
      })),
    },
  };
}
